import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { YouTubePlayerModule } from '@angular/youtube-player';
import { DateTime } from 'luxon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { ContentModalComponent } from '../../../basic/modal/content-modal/content-modal.component';
import { StripeProductStatus, StripeProductType } from '../../../core/stripe/product.stripe';
import { WorkspaceHolderService } from '../../../core/workspace/workspace.holder.service';
import { NewEditGoalComponent } from '../../../domain/goal/new-edit-goal/new-edit-goal.component';
import { NewEditMethodologyComponent } from '../../../domain/methodology/new-edit-methodology/new-edit-methodology.component';
import { EditResumeComponent } from '../../../domain/resume/edit-resume/edit-resume.component';
import { UploadResumeComponent } from '../../../domain/resume/upload-resume/upload-resume.component';
import { MainSidebarComponent } from '../main-sidebar/main-sidebar.component';

@Component({
  selector: 'app-basic-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    YouTubePlayerModule,

    NzStepsModule,
    NzButtonModule,
    NzIconModule,
    NzSkeletonModule,
    NzTagModule,
    NzAlertModule,

    MainSidebarComponent,

    EditResumeComponent,
    UploadResumeComponent,
    NewEditGoalComponent,
    NewEditMethodologyComponent,
    ContentModalComponent,
  ],
  templateUrl: './basic-page.component.html',
  styleUrl: './basic-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasicPageComponent {
  isMobile = signal<boolean>(false);

  isExpired = false;

  @Input() pageTitle?: string;
  @Input() pageDescription?: string;

  isPricingPage = computed(() => this.router.url === '/pricing');
  isSwitchWorkspacesPage = computed(() => this.router.url === '/switch-workspaces');

  paidStatus = computed(() => {
    return this.workspaceHolderService.paidStatus();
  });

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private workspaceHolderService: WorkspaceHolderService
  ) {}

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe((result) => {
      this.isMobile.set(result.matches);
    });
  }

  onBookDemoClick() {
    window.open('https://calendly.com/peakmojo-ai/book-a-demo', '_blank');
  }

  onUpgradeClick() {
    this.router.navigate(['/pricing']);
  }

  getTrialMessage = computed(() => {
    const paidStatus = this.workspaceHolderService.paidStatus();

    if (paidStatus?.status === StripeProductStatus.paid) {
      return '';
    }

    if (paidStatus?.status === StripeProductStatus.expired) {
      if (!this.isPricingPage()) {
        if (!this.isSwitchWorkspacesPage()) {
          this.isExpired = true;
        }
      }
    }

    const workspace = this.workspaceHolderService.currentWorkspace();
    if (workspace.publicSubData?.trialExpiredTimeStr) {
      const trialExpiredTime = DateTime.fromISO(workspace.publicSubData.trialExpiredTimeStr);
      const now = DateTime.local();

      if (trialExpiredTime > now) {
        const daysLeft = Math.ceil(trialExpiredTime.diff(now, 'days').days);

        if (daysLeft > 14) {
          return `You're on a free trial! Enjoy full access to ${paidStatus?.type === StripeProductType.premium ? 'all' : 'basic'} features. ${daysLeft} days left — upgrade anytime to keep exploring!`;
        }

        return `You're on a 14-day free trial! Enjoy full access to ${paidStatus?.type === StripeProductType.premium ? 'all' : 'basic'} features. ${daysLeft} days left — upgrade anytime to keep exploring!`;
      }
    }
    return '';
  });

  onSwitchWorkspaceClick() {
    this.router.navigate(['/switch-workspaces']);
  }
}
